import { useEffect, useState } from 'react';

import { useToast } from '@/src/components/Toast';
import {
  createGarageSharePost,
  garageVehicleShareURL,
  garageVehicleTitle,
  type GarageVehicle,
} from '@/src/features/garage/garage-service';
import {
  nativeShareDescription,
  nativeShareTitle,
  ShareOptionsModal,
  type ShareOption,
} from '@/src/features/sharing/ShareOptionsModal';
import {
  openFacebookShareDialog,
  shareLinkWithDevice,
} from '@/src/features/sharing/share-link';
import { trackEvent } from '@/src/lib/analytics';
import { copyTextToClipboard } from '@/src/utils/clipboard';

type GarageVehicleShareAction = 'copy' | 'facebook' | 'native' | 'reshare';

export function GarageVehicleShareModal({
  canShareOnFrienzzi,
  onClose,
  source,
  vehicle,
  visible,
}: {
  canShareOnFrienzzi?: boolean;
  onClose: () => void;
  source: 'garage_card' | 'garage_detail';
  vehicle: GarageVehicle;
  visible: boolean;
}) {
  const { showError, showSuccess } = useToast();
  const [sharingAction, setSharingAction] =
    useState<GarageVehicleShareAction | null>(null);
  const shareURL = garageVehicleShareURL(vehicle);
  const sharing = sharingAction != null;

  useEffect(() => {
    if (!visible) {
      setSharingAction(null);
    }
  }, [visible]);

  function beginShareAction(action: GarageVehicleShareAction) {
    if (!shareURL || sharing) {
      return false;
    }

    setSharingAction(action);
    trackEvent('garage_vehicle_share_start', { method: action, source });
    return true;
  }

  function finishShareAction() {
    setSharingAction(null);
  }

  function completeShare(method: string) {
    trackEvent('garage_vehicle_share_complete', { method, source });
  }

  async function copyVehicleLink() {
    if (!beginShareAction('copy') || !shareURL) {
      return;
    }

    try {
      await copyTextToClipboard(shareURL);
      completeShare('clipboard');
      showSuccess('Garage link copied.', 'Ready to share');
      onClose();
    } catch (error) {
      showError(error, 'Unable to copy that Garage link.');
    } finally {
      finishShareAction();
    }
  }

  async function shareVehicleWithDevice() {
    if (!beginShareAction('native') || !shareURL) {
      return;
    }

    const title = `${garageVehicleTitle(vehicle)} | Frienzzi Garage`;

    try {
      const result = await shareLinkWithDevice({
        title,
        text: `View ${garageVehicleTitle(vehicle)} on Frienzzi Garage.`,
        url: shareURL,
      });

      if (result.cancelled) {
        return;
      }

      completeShare(result.method);
      if (result.method === 'clipboard') {
        showSuccess('Garage link copied.', 'Ready to share');
      }
      onClose();
    } catch (error) {
      showError(error, 'Unable to share this Garage vehicle.');
    } finally {
      finishShareAction();
    }
  }

  async function shareVehicleToFacebook() {
    if (!beginShareAction('facebook') || !shareURL) {
      return;
    }

    try {
      await openFacebookShareDialog(shareURL);
      completeShare('facebook');
      showSuccess('Facebook share is ready.', 'Share opened');
      onClose();
    } catch (error) {
      showError(error, 'Unable to open Facebook sharing.');
    } finally {
      finishShareAction();
    }
  }

  async function shareVehicleOnFrienzzi() {
    if (!beginShareAction('reshare')) {
      return;
    }

    try {
      await createGarageSharePost(vehicle.id);
      completeShare('reshare');
      showSuccess('Your Garage vehicle is in the feed.', 'Shared on Frienzzi');
      onClose();
    } catch (error) {
      showError(error, 'Unable to share this Garage vehicle on Frienzzi.');
    } finally {
      finishShareAction();
    }
  }

  const options: ShareOption[] = [
    ...(canShareOnFrienzzi
      ? [
          {
            description:
              'Create a public feed post that features this Garage vehicle.',
            kind: 'reshare' as const,
            loading: sharingAction === 'reshare',
            onPress: () => {
              void shareVehicleOnFrienzzi();
            },
            title: 'Share on Frienzzi',
          },
        ]
      : []),
    {
      description: "Open Facebook with Frienzzi's share dialog.",
      kind: 'facebook',
      loading: sharingAction === 'facebook',
      onPress: () => {
        void shareVehicleToFacebook();
      },
      title: 'Share to Facebook',
    },
    {
      description: nativeShareDescription(),
      kind: 'native',
      loading: sharingAction === 'native',
      onPress: () => {
        void shareVehicleWithDevice();
      },
      title: nativeShareTitle(),
    },
    {
      description: 'Copy the Garage vehicle link so you can paste it anywhere.',
      kind: 'copy',
      loading: sharingAction === 'copy',
      onPress: () => {
        void copyVehicleLink();
      },
      title: 'Copy link',
    },
  ];

  return (
    <ShareOptionsModal
      busy={sharing}
      options={options}
      subtitle="Send this Garage vehicle to friends or share it outside Frienzzi."
      title="Share Garage vehicle"
      visible={visible}
      onClose={() => {
        if (!sharing) {
          onClose();
        }
      }}
    />
  );
}

